import { AuthGuard } from '@/components/auth/auth-guard';
import { AppLayout } from '@/components/layout';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { OverflowChips } from '@/components/ui/overflow-chips';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AssignCandidateDialog } from '@/features/recruitment/components/assign-candidate-dialog';
import {
  HireCandidateDialog,
  type HireCandidateInfo,
} from '@/features/recruitment/components/hire-candidate-dialog';
import { CandidateBulkActionBar } from '@/features/recruitment/components/candidate-bulk-action-bar';
import { CandidateStatusPill } from '@/features/recruitment/components/candidate-status-pill';
import { CreateCandidateDialog } from '@/features/recruitment/components/create-candidate-dialog';
import { GlobalCandidateSettingsDialog } from '@/features/recruitment/components/global-candidate-settings-dialog';
import { CANDIDATE_SOURCE_OPTIONS } from '@/features/recruitment/lib/candidate-source';
import {
  CONSENT_ENDING_OPTION,
  DEFAULT_CAND_SORT_BY,
  DEFAULT_CAND_SORT_ORDER,
  normalizeCandidatesSearch,
  sanitizeCandidatesSearch,
  type CandidateActiveVerified,
  type CandidateSortOrder,
  type CandidatesSearch,
} from '@/features/recruitment/lib/candidates-search';
import {
  CONSENT_ENDING_FILTER_HINT,
  CONSENT_ENDING_FILTER_LABEL,
  consentExpiryText,
  consentExpiryTone,
} from '@/features/recruitment/lib/consent-expiry';
import {
  awaitsAnonymisation,
  consentRowTone,
  isConsentLocked,
} from '@/features/recruitment/lib/consent-actions';
import { PermissionGate } from '@/components/auth/permission-gate';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useDebouncedValue } from '@/hooks/use-debounce';
import {
  useArchiveCandidate,
  useCandidateLocations,
  useCandidateTags,
  useCandidates,
  useHardDeleteCandidate,
} from '@/hooks/use-candidates';
import { canViewCandidates, useCanViewCandidates, usePermission } from '@/lib/permissions';
import { useAuthStore } from '@/stores/auth';
import type {
  CandidateListItem,
  CandidateQueryParams,
  CandidateSkill,
  CandidateStatus,
  CandidateVerifiedFilter,
  GdprStatus,
} from '@/types/candidates';
import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router';
import {
  ExternalLink,
  Link2,
  MoreHorizontal,
  Plus,
  Search,
  Settings,
  Trash2,
  UserPlus,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

const CAND_FILTERS_STORAGE_PREFIX = 'recruitment.candidates.filters:';

function readStoredCandidateFilters(userId: string | undefined): CandidatesSearch {
  if (!userId || typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(`${CAND_FILTERS_STORAGE_PREFIX}${userId}`);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    return sanitizeCandidatesSearch(parsed as Record<string, unknown>);
  } catch {
    return {};
  }
}

function writeStoredCandidateFilters(userId: string | undefined, search: CandidatesSearch) {
  if (!userId || typeof window === 'undefined') return;
  const key = `${CAND_FILTERS_STORAGE_PREFIX}${userId}`;
  try {
    if (Object.keys(search).length === 0) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, JSON.stringify(search));
  } catch {
    // localStorage may be unavailable (private mode / quota); persistence is
    // best-effort — the URL still holds the live selection.
  }
}

export const Route = createFileRoute('/recruitment/candidates')({
  beforeLoad: () => {
    const checkGate = () => {
      const { isAuthenticated, user } = useAuthStore.getState();
      if (!isAuthenticated) throw redirect({ to: '/login' });
      if (!canViewCandidates(user)) throw redirect({ to: '/people' });
    };
    if (useAuthStore.getState().isInitialized) {
      checkGate();
      return;
    }
    return useAuthStore.getState().initialize().then(checkGate);
  },
  validateSearch: (search: Record<string, unknown>): CandidatesSearch =>
    sanitizeCandidatesSearch(search),
  component: CandidatesPage,
});

// HW-705: derived from the shared option list so the candidates-list Source
// column stays in sync with the New Candidate dialog and profile Contact card.
const SOURCE_LABELS: Record<string, string> = Object.fromEntries(
  CANDIDATE_SOURCE_OPTIONS.map((o) => [o.value, o.label]),
);

// Light theme uses the bold `.es-*` palette (solid teal/amber/red on cream
// text) — same treatment as the employee profile hero card. `.dark .es-*` in
// globals.css restores the soft tinted look in dark mode. Border made
// transparent so the solid pill bg isn't framed by a clashing outline.
const GDPR_COLORS: Record<GdprStatus, string> = {
  APPROVED: 'es-active border-transparent',
  PENDING: 'es-probation border-transparent',
  EXPIRED: 'es-terminated border-transparent',
  REVOKED: 'es-terminated border-transparent',
};

function CandidatesPage() {
  const navigate = useNavigate();
  // Honour the scoped view codes (`view.assigned` / `view.all`), not just the
  // legacy `view` — matches the route gate so a hiring-team member with
  // `view.assigned` isn't blocked by the in-page permission check.
  const canView = useCanViewCandidates();
  const canEdit = usePermission('recruitment.candidates.edit');
  const canCreate = usePermission('recruitment.candidates.create');
  const canDelete = usePermission('recruitment.candidates.delete');
  const canHire = usePermission('recruitment.candidates.hire');

  const urlSearch = Route.useSearch();
  const userId = useAuthStore((s) => s.user?.id);

  // URL query is the source of truth; localStorage mirrors it per user (HW-590).
  const status: CandidateStatus | 'ALL' = urlSearch.status ?? 'ALL';
  const city = urlSearch.city ?? 'ALL';
  const tag = urlSearch.tag ?? 'ALL';
  const verified: CandidateVerifiedFilter = urlSearch.verified ?? 'any';
  const gdprStatus: GdprStatus | 'ALL' = urlSearch.gdprStatus ?? 'ALL';
  const consentExpiry = urlSearch.consentExpiry;
  const gdprSelectValue = consentExpiry ? CONSENT_ENDING_OPTION : gdprStatus;
  const sortBy = urlSearch.sortBy ?? DEFAULT_CAND_SORT_BY;
  const sortOrder = urlSearch.sortOrder ?? DEFAULT_CAND_SORT_ORDER;

  // The search box stays local for snappy typing; pushed to the URL debounced.
  const [search, setSearch] = useState(urlSearch.q ?? '');
  const [page, setPage] = useState(1);

  const [assigningCandidate, setAssigningCandidate] = useState<CandidateListItem | null>(null);
  const [hiringCandidate, setHiringCandidate] = useState<HireCandidateInfo | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);

  const debouncedSearch = useDebouncedValue(search, 300);
  const limit = 20;

  const applySearch = (patch: Partial<CandidatesSearch>) => {
    setPage(1);
    void navigate({
      to: '/recruitment/candidates',
      // `prev` is typed as the router-wide search union; it's this route's
      // schema at runtime (guaranteed by validateSearch), so narrow it before
      // merging the patch.
      search: (prev) => normalizeCandidatesSearch({ ...(prev as CandidatesSearch), ...patch }),
      replace: true,
    });
  };

  // One-shot hydration: when the URL carries no filters (e.g. straight after
  // re-login) fall back to the per-user localStorage snapshot. Order is
  // URL → localStorage → default. Gated on a state flag (not a ref) so the mirror
  // effect never observes the pre-hydration empty URL and wipes the snapshot.
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    if (hydrated || !userId) return;
    if (Object.keys(urlSearch).length === 0) {
      const stored = readStoredCandidateFilters(userId);
      if (Object.keys(stored).length > 0) {
        setSearch(stored.q ?? '');
        void navigate({ to: '/recruitment/candidates', search: stored, replace: true });
      }
    }
    setHydrated(true);
  }, [userId]);

  // Mirror the live URL selection into localStorage for next time.
  const storedKey = JSON.stringify(urlSearch);
  useEffect(() => {
    if (hydrated && userId) writeStoredCandidateFilters(userId, urlSearch);
  }, [hydrated, userId, storedKey]);

  // Keep the input in sync when the URL `q` changes from outside the box
  // (hydration / back-forward). A no-op during active typing.
  useEffect(() => {
    setSearch(urlSearch.q ?? '');
  }, [urlSearch.q]);

  // Push the debounced search text into the URL.
  useEffect(() => {
    if (debouncedSearch === (urlSearch.q ?? '')) return;
    applySearch({ q: debouncedSearch || undefined });
  }, [debouncedSearch]);

  // Reset selection when filter/search context changes (page+sort don't reset).
  useEffect(() => {
    setSelected(new Set());
  }, [debouncedSearch, status, city, tag, verified, gdprStatus, consentExpiry]);

  const params: CandidateQueryParams = useMemo(
    () => ({
      page,
      limit,
      search: debouncedSearch || undefined,
      status: status === 'ALL' ? undefined : status,
      city: city === 'ALL' ? undefined : city,
      tag: tag === 'ALL' ? undefined : tag,
      verified: verified === 'any' ? undefined : verified,
      gdprStatus: gdprStatus === 'ALL' ? undefined : gdprStatus,
      consentExpiry,
      sortBy,
      sortOrder,
    }),
    [
      page,
      debouncedSearch,
      status,
      city,
      tag,
      verified,
      gdprStatus,
      consentExpiry,
      sortBy,
      sortOrder,
    ]
  );

  const { data, isLoading } = useCandidates(params);
  const { data: locations = [] } = useCandidateLocations();
  const { data: tagOptions = [] } = useCandidateTags();
  const archiveMutation = useArchiveCandidate();
  const hardDeleteMutation = useHardDeleteCandidate();
  const [deleteCandidate, setDeleteCandidate] = useState<CandidateListItem | null>(null);

  const candidates = data?.data ?? [];
  const meta = data?.meta;

  // City and Tag <Select>s render every option as a child element. With 2k+
  // distinct locations in our PF-imported pool, naive inline `.map()` runs
  // ~2.2k React.createElement calls on EVERY parent re-render — and the
  // search input forces a re-render on every keystroke. Memoize the JSX
  // children so typing in search doesn't drag the whole dropdown tree
  // through reconciliation each time.
  const locationItems = useMemo(
    () =>
      locations.map((loc) => (
        <SelectItem key={loc} value={loc} className="[&>span:last-child]:truncate" title={loc}>
          {loc}
        </SelectItem>
      )),
    [locations]
  );
  const tagItems = useMemo(
    () =>
      tagOptions.map((t) => (
        <SelectItem key={t} value={t} className="[&>span:last-child]:truncate" title={`#${t}`}>
          #{t}
        </SelectItem>
      )),
    [tagOptions]
  );

  const pageIds = useMemo(() => candidates.map((c) => c.id), [candidates]);
  const pageSelectedCount = useMemo(
    () => pageIds.reduce((acc, id) => acc + (selected.has(id) ? 1 : 0), 0),
    [pageIds, selected]
  );
  const pageState: 'none' | 'partial' | 'all' =
    pageIds.length === 0 || pageSelectedCount === 0
      ? 'none'
      : pageSelectedCount === pageIds.length
        ? 'all'
        : 'partial';

  const toggleOne = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const togglePage = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (pageState === 'all') {
        // Remove only the current page IDs; leave other-page selections untouched.
        for (const id of pageIds) next.delete(id);
      } else {
        // Add all current page IDs.
        for (const id of pageIds) next.add(id);
      }
      return next;
    });
  };

  const activeFilterCount =
    (debouncedSearch ? 1 : 0) +
    (status !== 'ALL' ? 1 : 0) +
    (city !== 'ALL' ? 1 : 0) +
    (tag !== 'ALL' ? 1 : 0) +
    (verified !== 'any' ? 1 : 0) +
    (gdprSelectValue !== 'ALL' ? 1 : 0);

  const clearFilters = () => {
    setSearch('');
    setPage(1);
    void navigate({ to: '/recruitment/candidates', search: {}, replace: true });
  };

  const handleSort = (field: CandidateQueryParams['sortBy']) => {
    if (!field) return;
    const nextOrder: CandidateSortOrder =
      sortBy === field ? (sortOrder === 'asc' ? 'desc' : 'asc') : 'asc';
    applySearch({ sortBy: field, sortOrder: nextOrder });
  };

  const SortHeader = ({
    field,
    children,
  }: {
    field: CandidateQueryParams['sortBy'];
    children: React.ReactNode;
  }) => (
    <th
      className="px-4 py-3 text-left text-sm font-medium text-muted-foreground cursor-pointer hover:text-foreground select-none"
      onClick={() => handleSort(field)}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {sortBy === field && <span className="text-xs">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
      </span>
    </th>
  );

  if (!canView) {
    return (
      <AuthGuard>
        <AppLayout>
          <div className="flex-1 p-6">
            <div className="rounded-md border border-border bg-muted/30 p-6 text-center text-sm text-muted-foreground">
              You do not have permission to view candidates.
            </div>
          </div>
        </AppLayout>
      </AuthGuard>
    );
  }

  return (
    <AuthGuard>
      <AppLayout>
        <header className="sticky top-14 z-30 flex h-12 items-center justify-between border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 px-6">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>Home</span>
            <span>/</span>
            <span>Recruitment</span>
            <span>/</span>
            <span className="font-medium text-foreground">Candidates</span>
          </div>
          <PermissionGate permission="recruitment.skills.manage">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-foreground"
                    onClick={() => setSettingsOpen(true)}
                  >
                    <Settings className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Skill aliases</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </PermissionGate>
        </header>

        <GlobalCandidateSettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />

        <div className="flex-1 space-y-4 p-6">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-semibold tracking-tight text-foreground">Candidates</h1>
            <div className="flex items-center gap-3">
              <div className="text-sm text-muted-foreground">
                {meta ? `${meta.total} total` : ''}
              </div>
              {canCreate && (
                <Button variant="accent" onClick={() => setCreateOpen(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  New Candidate
                </Button>
              )}
            </div>
          </div>

          {/* Filters */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[220px] max-w-md">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by name or skill..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="pl-9"
              />
            </div>

            <Select
              value={status}
              onValueChange={(v) =>
                applySearch({ status: v === 'ALL' ? undefined : (v as CandidateStatus) })
              }
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All statuses</SelectItem>
                <SelectItem value="ACTIVE">Active</SelectItem>
                <SelectItem value="HIRING">Hiring</SelectItem>
                <SelectItem value="HIRED">Hired</SelectItem>
                <SelectItem value="ARCHIVED">Archived</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={city}
              onValueChange={(v) => applySearch({ city: v === 'ALL' ? undefined : v })}
            >
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="City" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All cities</SelectItem>
                {locationItems}
              </SelectContent>
            </Select>

            <Select
              value={tag}
              onValueChange={(v) => applySearch({ tag: v === 'ALL' ? undefined : v })}
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Tag" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All tags</SelectItem>
                {tagItems}
              </SelectContent>
            </Select>

            <Select
              value={verified}
              onValueChange={(v) =>
                applySearch({ verified: v === 'any' ? undefined : (v as CandidateActiveVerified) })
              }
            >
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Verified" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any skills</SelectItem>
                <SelectItem value="verified">Has verified</SelectItem>
                <SelectItem value="unverified">All unverified</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={gdprSelectValue}
              onValueChange={(v) =>
                applySearch(
                  v === CONSENT_ENDING_OPTION
                    ? { gdprStatus: undefined, consentExpiry: 'ending' }
                    : {
                        gdprStatus: v === 'ALL' ? undefined : (v as GdprStatus),
                        consentExpiry: undefined,
                      }
                )
              }
            >
              <SelectTrigger
                className={consentExpiry ? 'w-[190px]' : 'w-[140px]'}
                title={consentExpiry ? CONSENT_ENDING_FILTER_HINT : undefined}
              >
                <SelectValue placeholder="GDPR" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All GDPR</SelectItem>
                <SelectItem value="APPROVED">Approved</SelectItem>
                <SelectItem value="PENDING">Pending</SelectItem>
                <SelectItem value="EXPIRED">Expired</SelectItem>
                <SelectItem value="REVOKED">Revoked</SelectItem>
                <SelectItem value={CONSENT_ENDING_OPTION} title={CONSENT_ENDING_FILTER_HINT}>
                  {CONSENT_ENDING_FILTER_LABEL}
                </SelectItem>
              </SelectContent>
            </Select>

            {activeFilterCount > 0 && (
              <Button variant="outline" size="sm" onClick={clearFilters}>
                Clear filters
              </Button>
            )}
          </div>

          {/* Table */}
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full">
              <thead className="border-b bg-muted/50">
                <tr>
                  {canEdit && (
                    <th className="w-[44px] px-4 py-3">
                      <Checkbox
                        checked={
                          pageState === 'all'
                            ? true
                            : pageState === 'partial'
                              ? 'indeterminate'
                              : false
                        }
                        onCheckedChange={() => togglePage()}
                        aria-label="Select all candidates on this page"
                        disabled={pageIds.length === 0}
                        className="cursor-pointer"
                      />
                    </th>
                  )}
                  <SortHeader field="fullName">Candidate</SortHeader>
                  <SortHeader field="status">Status</SortHeader>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                    Source
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                    Location
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                    Skills
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                    GDPR
                  </th>
                  <SortHeader field="updatedAt">Last activity</SortHeader>
                  <th className="px-4 py-3 w-[50px]" />
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={canEdit ? 9 : 8} className="px-4 py-8 text-center text-muted-foreground">
                      Loading...
                    </td>
                  </tr>
                ) : candidates.length === 0 ? (
                  <tr>
                    <td colSpan={canEdit ? 9 : 8} className="px-4 py-8 text-center text-muted-foreground">
                      {activeFilterCount > 0
                        ? 'No candidates match your filters.'
                        : 'No candidates yet.'}
                    </td>
                  </tr>
                ) : (
                  candidates.map((c) => (
                    <CandidateRow
                      key={c.id}
                      candidate={c}
                      canEdit={canEdit}
                      canDelete={canDelete}
                      isSelected={selected.has(c.id)}
                      onToggleSelect={() => toggleOne(c.id)}
                      onAssign={() => setAssigningCandidate(c)}
                      onArchive={() => {
                        if (confirm(`Archive candidate ${c.fullName}?`)) {
                          archiveMutation.mutate(c.id);
                        }
                      }}
                      onDelete={() => setDeleteCandidate(c)}
                      canHire={canHire}
                      onHire={() =>
                        setHiringCandidate({
                          id: c.id,
                          firstName: c.firstName,
                          lastName: c.lastName,
                          email: c.email,
                        })
                      }
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {meta && meta.totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Showing {candidates.length} of {meta.total}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                >
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground">
                  Page {meta.page} of {meta.totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= meta.totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}

          <AssignCandidateDialog
            candidate={assigningCandidate}
            open={!!assigningCandidate}
            onOpenChange={(open) => !open && setAssigningCandidate(null)}
          />

          <HireCandidateDialog
            open={!!hiringCandidate}
            candidate={hiringCandidate}
            onOpenChange={(open) => !open && setHiringCandidate(null)}
            onHired={() => setHiringCandidate(null)}
            onCancelled={() => {}}
          />

          <CreateCandidateDialog
            open={createOpen}
            onOpenChange={setCreateOpen}
            onSuccess={(id) => navigate({ to: '/recruitment/candidates/$id', params: { id } })}
          />

          <AlertDialog
            open={!!deleteCandidate}
            onOpenChange={(open) => !open && setDeleteCandidate(null)}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle className="wrap-anywhere">
                  Permanently delete {deleteCandidate?.fullName}?
                </AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently remove the candidate record together with all assignments,
                  notes, interviews and uploaded files. This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={hardDeleteMutation.isPending}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  disabled={hardDeleteMutation.isPending}
                  onClick={(e) => {
                    e.preventDefault();
                    if (!deleteCandidate) return;
                    hardDeleteMutation.mutate(deleteCandidate.id, {
                      onSuccess: () => setDeleteCandidate(null),
                    });
                  }}
                >
                  {hardDeleteMutation.isPending ? 'Deleting…' : 'Delete'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {canEdit && (
            <>
              <CandidateBulkActionBar
                count={selected.size}
                onAssign={() => setBulkAssignOpen(true)}
                onClear={() => setSelected(new Set())}
              />
              <AssignCandidateDialog
                candidateIds={Array.from(selected)}
                open={bulkAssignOpen}
                onOpenChange={setBulkAssignOpen}
                onSuccess={() => setSelected(new Set())}
              />
            </>
          )}
        </div>
      </AppLayout>
    </AuthGuard>
  );
}

/**
 * A skill chip. Clamped in the Skills cell, where the column is too narrow to seat a long
 * name and a chip left free to grow would widen it; spelled out in full inside the `+N`
 * popover, which has the room. The cap is a fixed width, not `max-w-full`: a percentage
 * resolves against the column, and the column is sized by this very content, so it would
 * not constrain the chip at all.
 */
function renderSkillChip(skill: CandidateSkill, { clamp }: { clamp: boolean }) {
  return (
    <Badge
      variant={skill.verified ? 'default' : 'outline'}
      // Hover reveals what the clamp hides. The `+N` popover cannot serve as that read
      // path — it holds only the skills the cell leaves out, never a shortened one it shows.
      title={clamp ? skill.name : undefined}
      // The popover exists to show the name in full, so the unclamped chip wraps
      // instead of truncating — without this it inherits the badge's global
      // `whitespace-nowrap` and a long skill runs straight out of the 320px panel.
      className={`text-xs font-normal ${clamp ? 'max-w-[140px]' : 'whitespace-normal break-words'} ${
        skill.verified
          ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300'
          : ''
      }`}
    >
      {clamp ? <span className="truncate">{skill.name}</span> : skill.name}
    </Badge>
  );
}

interface CandidateRowProps {
  candidate: CandidateListItem;
  canEdit: boolean;
  canDelete: boolean;
  isSelected: boolean;
  onToggleSelect: () => void;
  onAssign: () => void;
  onArchive: () => void;
  onDelete: () => void;
  canHire: boolean;
  onHire: () => void;
}

function CandidateRow({
  candidate,
  canEdit,
  canDelete,
  isSelected,
  onToggleSelect,
  onAssign,
  onArchive,
  onDelete,
  canHire,
  onHire,
}: CandidateRowProps) {
  const initials = candidate.fullName
    .split(' ')
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <tr className={`border-b hover:bg-muted/50 ${consentRowTone(candidate)}`}>
      {canEdit && (
        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={isSelected}
            onCheckedChange={onToggleSelect}
            aria-label={`Select ${candidate.fullName}`}
          />
        </td>
      )}
      <td className="px-4 py-3">
        <Link
          to="/recruitment/candidates/$id"
          params={{ id: candidate.id }}
          className="flex items-center gap-3 hover:text-primary"
        >
          <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center text-xs font-medium shrink-0">
            {candidate.photoUrl ? (
              <img
                src={candidate.photoUrl}
                alt=""
                className="h-full w-full rounded-full object-cover"
              />
            ) : (
              initials
            )}
          </div>
          <div className="min-w-0 max-w-xs">
            <div className="font-medium truncate" title={candidate.fullName}>
              {candidate.fullName}
            </div>
            {candidate.headline && (
              <div className="text-xs text-muted-foreground truncate" title={candidate.headline}>
                {candidate.headline}
              </div>
            )}
          </div>
        </Link>
      </td>
      <td className="px-4 py-3">
        <CandidateStatusPill
          candidateId={candidate.id}
          status={candidate.status}
          size="sm"
          onHire={canHire && candidate.activeAssignments.length > 0 ? onHire : undefined}
          hiredDisabledReason={
            canHire && candidate.activeAssignments.length === 0
              ? 'Assign the candidate to a vacancy first'
              : undefined
          }
          consentRevoked={isConsentLocked(candidate)}
        />
      </td>
      <td className="px-4 py-3 text-sm text-muted-foreground">
        {candidate.source ? (SOURCE_LABELS[candidate.source] ?? candidate.source) : '—'}
      </td>
      <td className="px-4 py-3 text-sm text-muted-foreground">
        <div className="max-w-[200px] truncate" title={candidate.location ?? undefined}>
          {candidate.location ?? '—'}
        </div>
      </td>
      <td className="px-4 py-3">
        {candidate.skills.length === 0 ? (
          <span className="text-sm text-muted-foreground">—</span>
        ) : (
          <OverflowChips
            items={candidate.skills}
            max={3}
            getKey={(s) => s.id}
            renderChip={(s) => renderSkillChip(s, { clamp: true })}
            renderOverflowChip={(s) => renderSkillChip(s, { clamp: false })}
            ariaLabel={(hidden) =>
              `Show ${hidden.length} more skills: ${hidden.map((s) => s.name).join(', ')}`
            }
          />
        )}
      </td>
      <td className="px-4 py-3">
        <Badge variant="outline" className={`font-normal ${GDPR_COLORS[candidate.gdprStatus]}`}>
          {candidate.gdprStatus.charAt(0) + candidate.gdprStatus.slice(1).toLowerCase()}
        </Badge>
        {/* HW-1055: consent revoked, the data still waits to be erased. */}
        {awaitsAnonymisation(candidate) && (
          <div className="mt-1 whitespace-nowrap text-xs text-destructive">Anonymise pending</div>
        )}
        {/* HW-1023: when the consent ends — amber within 30 days, red once ended. */}
        {candidate.consentExpiry && (
          <div
            className={`mt-1 whitespace-nowrap text-xs ${consentExpiryTone(candidate.consentExpiry)}`}
          >
            {consentExpiryText(candidate.consentExpiry)}
          </div>
        )}
      </td>
      <td className="px-4 py-3 text-sm text-muted-foreground">
        {new Date(candidate.updatedAt).toLocaleDateString()}
      </td>
      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-muted-foreground"
              aria-label={`Actions for ${candidate.fullName}`}
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {canEdit && (
              // HW-1055: a candidate who revoked consent is not added to vacancies.
              <DropdownMenuItem onClick={onAssign} disabled={isConsentLocked(candidate)}>
                <UserPlus className="mr-2 h-4 w-4" />
                {isConsentLocked(candidate) ? 'Assign to vacancy — consent revoked' : 'Assign to vacancy…'}
              </DropdownMenuItem>
            )}
            {candidate.linkedinUrl && (
              <DropdownMenuItem
                onClick={() => window.open(candidate.linkedinUrl!, '_blank', 'noopener')}
              >
                <Link2 className="mr-2 h-4 w-4" />
                Open LinkedIn
              </DropdownMenuItem>
            )}
            {/* HW-1055: a candidate who revoked consent is not contacted. */}
            {candidate.email && !isConsentLocked(candidate) && (
              <DropdownMenuItem onClick={() => window.open(`mailto:${candidate.email}`, '_blank')}>
                <ExternalLink className="mr-2 h-4 w-4" />
                Send email
              </DropdownMenuItem>
            )}
            {canEdit && candidate.status !== 'ARCHIVED' && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={onArchive}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Archive
                </DropdownMenuItem>
              </>
            )}
            {canDelete && (
              <>
                {!(canEdit && candidate.status !== 'ARCHIVED') && <DropdownMenuSeparator />}
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={onDelete}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </td>
    </tr>
  );
}

